<script setup>
import { computed } from 'vue'
import StreamPlayer from '../StreamPlayer.vue'
import IngestStatus from './IngestStatus.vue'
import { derivePreviewHost } from '@/Utils/previewHost'

// A single ingest's live preview (primary / backup / final output). Builds the
// MSE playback URL from the base stream key plus an optional suffix and gates on
// that ingest's own alive flag + pulse. The host is resolved from the pulse's
// hostId inside StreamPlayer, so the template host below is just a placeholder.
const props = defineProps({
	stream: { type: Object, required: true },
	// '' = combined output, '_primary' / '_backup' = failover ingests.
	suffix: { type: String, default: '' },
	label: { type: String, default: 'Preview' },
	mediaPulse: { type: Object, default: () => ({}) },
	alive: { type: Boolean, default: false },
	// Unique id so multiple MSE players can coexist on one page.
	refLink: { type: Number, required: true },
})

const previewKey = computed(() => `${props.stream.key}${props.suffix}`)
// Host comes from the stats/pulse response (mediaPulse.hostId). Until it's known
// we emit an empty url so the template shows the waiting state instead of a
// preview pointed at the dead staging host.
const mseUrl = computed(() => {
	const host = derivePreviewHost(props.mediaPulse, props.stream)
	if (!host) return ''
	return `wss://${host}/${previewKey.value}/mse_ld?tracks=v1a1`
})

// Live encoder specs for this ingest (so users can verify primary/backup match).
const specs = computed(() => {
	const p = props.mediaPulse || {}
	if (!props.alive) return []
	const parts = []
	if (p.width && p.height) parts.push(`${p.width}×${p.height}`)
	if (p.fps) parts.push(`${Math.round(p.fps)} fps`)
	const codecs = Array.isArray(p.codecs) ? p.codecs.map((c) => c.codec).filter(Boolean).join('/') : ''
	if (codecs) parts.push(codecs)
	if (p.bitrate) parts.push(`${p.bitrate} kbps`)
	return parts
})
</script>

<template>
	<div class="flex flex-col gap-2">
		<div class="flex items-center gap-2">
			<span class="text-sm font-medium text-surface-9">{{ label }}</span>
			<IngestStatus :alive="alive" class="ml-auto" />
		</div>
		<div class="relative aspect-video w-full overflow-hidden rounded-md bg-black">
			<div v-if="!alive || !mseUrl" class="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
				<p class="text-surface-8 font-medium mb-1 text-sm">Waiting for stream</p>
				<p class="text-tiny text-surface-6 mb-0">Preview appears once this encoder connects.</p>
			</div>
			<stream-player
				v-else
				:stream="stream"
				:stream-url="mseUrl"
				:preview-key="previewKey"
				:media-pulse="mediaPulse"
				:ref-link="refLink"
				class="ingest-player w-full h-full"
			/>
		</div>
		<p v-if="specs.length" class="text-tiny text-surface-7 m-0 truncate" :title="specs.join(' · ')">
			{{ specs.join(' · ') }}
		</p>
	</div>
</template>

<style scoped>
/* The shared StreamPlayer forces a fixed 236px .player-container height; make it
   fill our 16:9 box instead so the video isn't cropped/letterboxed oddly. */
.ingest-player :deep(.player-container) {
	height: 100%;
}
.ingest-player :deep(#player),
.ingest-player :deep(.player-container video) {
	width: 100%;
	height: 100%;
	object-fit: cover;
	border-radius: 0;
}
</style>

